import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, LogOut, Settings, Filter, Users, UserCircle } from 'lucide-react';
import { auth } from '../utils/auth';
import { useLanguage } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const checkAuth = useCallback(async () => {
    const token = auth.getToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      toast.error(t('toastSessionExpired'));
      
      
      navigate('/admin/login');
    }
  }, [t, navigate]);

  const fetchLeads = useCallback(async () => {
    const token = auth.getToken();
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setLeads(response.data);
    } catch (error) {
      toast.error(t('toastLeadsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    checkAuth();
    fetchLeads();
  }, [checkAuth, fetchLeads]);

  const handleLogout = async () => {
    await auth.logout();
    toast.success(t('toastLogoutSuccess'));
    navigate('/admin/login');
  };

  const exportCSV = async () => {
    const token = auth.getToken();
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const response = await axios.get(`${API}/leads/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('toastCsvSuccess'));
    } catch (error) {
      toast.error(t('toastCsvError'));
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      new: 'status-new',
      contacted: 'status-contacted',
      weitergeleitet: 'status-weitergeleitet',
      qualified: 'status-qualified',
      converted: 'status-converted',
      rejected: 'status-rejected',
      delete: 'status-rejected'
    };
    return `status-badge ${classes[status] || ''}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency) => {
    return `${Math.round(amount).toLocaleString('de-DE')} ${currency || 'EUR'}`;
  };

  return (
    <div className="admin-container" data-testid="admin-dashboard">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img
            src="https://customer-assets.emergentagent.com/job_lead-wizard-5/artifacts/xohrqn39_Logo%20separatus%20vergleich.png"
            alt="separatus Vergleich"
            style={{ maxWidth: '200px' }}
          />
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1B5E20' }}>
            separatus-kostenrechner <span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#666' }}>v1.0.0</span>
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {(auth.getUser() || {}).role === 'admin' && (
            <Link to="/admin/users" data-testid="users-link">
              <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users size={18} /> Benutzer
              </button>
            </Link>
          )}
          <Link to="/admin/settings" data-testid="settings-link">
            <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Settings size={18} /> Einstellungen
            </button>
          </Link>
          <Link to="/admin/profile" data-testid="profile-link">
            <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <UserCircle size={18} /> Profil
            </button>
          </Link>
          <button onClick={handleLogout} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="logout-btn">
            <LogOut size={18} /> Abmelden
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600' }}>Leads (Übersicht)</h2>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Filter size={18} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-select"
                  style={{ width: 'auto', minWidth: '150px' }}
                  data-testid="status-filter"
                >
                  <option value="">Alle Status</option>
                  <option value="neu">Neu</option>
                  <option value="contacted">Kontaktiert</option>
                  <option value="weitergeleitet">Weitergeleitet</option>
                  <option value="qualified">konkret Planen</option>
                  <option value="converted">in Umsetzung</option>
                  <option value="rejected">on Hold</option>
                  <option value="delete">Lead löschen</option>
                  <option value="waitlist">Warteliste</option>
                </select>
              </div>
              <button onClick={exportCSV} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="export-csv-btn">
                <Download size={18} /> CSV Export
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Lade Leads...</div>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Keine Leads gefunden</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th>PLZ</th>
                    <th>Projekt</th>
                    <th>Heizleistung kW</th>
                    <th>Status</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id} data-testid={`lead-row-${lead.id}`} style={lead.status === 'waitlist' ? { background: '#FFF9E6' } : {}}>
                      <td>{formatDate(lead.created_at)}</td>
                      <td>
                        {lead.name}
                      </td>
                      <td>{lead.email}</td>
                      <td>{lead.plz}</td>
                      <td>{lead.project_type ? (lead.project_type === 'neubau' ? 'Neubau' : 'Tausch') : '-'}</td>
                      <td>{(() => {
                        if (lead.calc_json) {
                          try {
                            const calc = typeof lead.calc_json === 'string' ? JSON.parse(lead.calc_json) : lead.calc_json;
                            if (calc.leistungsbedarf != null) return Number(calc.leistungsbedarf).toFixed(1);
                          } catch(e) { console.error('Parse error:', e); }
                        }
                        return '-';
                      })()}</td>
                      <td>
                        <span className={getStatusBadgeClass(lead.status)}>
                          {lead.status === 'new' && 'Neu'}
                          {lead.status === 'neu' && 'Neu'}
                          {lead.status === 'contacted' && 'Kontaktiert'}
                          {lead.status === 'qualified' && 'konkret Planen'}
                          {lead.status === 'converted' && 'in Umsetzung'}
                          {lead.status === 'rejected' && 'on Hold'}
                          {lead.status === 'delete' && 'Lead löschen'}
                          {lead.status === 'waitlist' && 'Warteliste'}
                        </span>
                      </td>
                      <td>
                        <Link to={`/admin/leads/${lead.id}`}>
                          <button className="btn-secondary" style={{ padding: '0.5rem 1rem' }} data-testid={`view-lead-${lead.id}`}>
                            Details
                          </button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
